import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Container, Row, Col, ListGroup, Form } from "react-bootstrap";
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { AuthContext } from "../../Context/AuthContext";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const TransactionProfile = () => {
  const [ranges, setRanges] = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [vfcData, setVfcData] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [transactions, setTransactions] = useState([]);
  const { access } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    setLoading(true);
    const fetchRanges = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, Config);
        const data = response.data;

        if (Array.isArray(data)) {
          setRanges(data);
        } else {
          console.error("API did not return an array", data);
        }
      } catch (err) {
        console.error("Error fetching data", err);
      }
      setLoading(false);
    };

    fetchRanges();
  }, [access]);

  useEffect(() => {
    setLoading(true);
    if (selectedRange) {
      const fetchVfcsByRange = async () => {
        try {
          const Config = Authorization();
          const response = await axios.post(
            `${Api}/api/vfcs/vfcs-by-range`,
            {
              rangeId: selectedRange,
            },
            Config
          );
          setVfcData(response.data.vfcs);
        } catch (error) {
          console.error("Error fetching VFCs", error);
        }
        setLoading(false);
      };

      fetchVfcsByRange();
    } else {
      setVfcData([]);
    }
  }, [selectedRange]);

  const handleViewClick = async () => {
    setLoading(true);
    if (selectedRange && selectedVfc) {
      try {
        const Config = Authorization();
        const response = await axios.post(
          `${Api}/api/transactions/getTransactionByVFC`,
          { vfcId: selectedVfc },
          Config
        );
        setTransactions(response.data);
      } catch (error) {
        console.error("Error fetching transactions", error);
      }
    }
    setLoading(false);
  };

  const handleRangeChange = (event) => {
    setSelectedRange(event.target.value);
  };

  const handleVfcChange = (event) => {
    setSelectedVfc(event.target.value);
  };
  // const selectedVfcData = vfcData.find((vfc) => vfc._id === selectedVfc);
  const selectedTransaction = transactions.find(
    (txn) => txn.vfcId && txn.vfcId._id === selectedVfc
  );
  const selectedVfcData = selectedTransaction
    ? selectedTransaction.vfcId
    : vfcData.find((vfc) => vfc._id === selectedVfc);

  const receiptData = transactions
    .filter((txn) => txn.type === "R")
    .map((txn) => ({ name: txn.transactionDate, value: txn.amount }));

  const paymentData = transactions
    .filter((txn) => txn.type === "P")
    .map((txn) => ({ name: txn.transactionDate, value: txn.amount }));

  const transactionTrendData = transactions.map((txn) => ({
    date: new Date(txn.transactionDate).toLocaleDateString(),
    amount: txn.amount,
  }));

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title"> Transaction Profile </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Transaction Profile
            </li>
          </ol>
        </nav>
      </div>

      <Container className="my-5">
        <>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <Form>
                  <Row>
                    <Col xs={6}>
                      <Form.Group controlId="Range" className="mb-3">
                        <Form.Label>Range</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="select"
                            value={selectedRange}
                            onChange={handleRangeChange}
                          >
                            <option value="">Select Range</option>
                            {ranges.map((range) => (
                              <option key={range._id} value={range._id}>
                                {range.rangeName}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group controlId="vfcName" className="mb-3">
                        <Form.Label>VFC Name</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="select"
                            value={selectedVfc}
                            onChange={handleVfcChange}
                          >
                            <option value="">Select VFC</option>
                            {vfcData.map((vfc) => (
                              <option key={vfc._id} value={vfc._id}>
                                {vfc.vfcName}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-icon-text mt-1"
                    onClick={handleViewClick}
                  >
                    <i className="mdi mdi-file-check btn-icon-prepend"></i>
                    View
                  </button>
                </Form>
              </div>
            </div>
          </div>

          <Row>
            <Col md={4}>
              <div className="card">
                <div className="card-body">
                  <p className="card-description">VFC Transaction Details</p>
                  <p className="card-description">
                    {vfcData.find((vfc) => vfc._id === selectedVfc)?.vfcName ||
                      "VFC Account"}
                  </p>

                  {selectedVfcData && (
                    <div>
                      <p>
                        <strong>Branch:</strong>
                        {selectedVfcData?.branch || "N/A"}
                      </p>
                      <p>
                        <strong>Account Number:</strong>
                        {selectedVfcData?.accountNumber || "N/A"}
                      </p>
                      <p>
                        <strong>IFSC Code:</strong>
                        {selectedVfcData?.ifscCode || "N/A"}
                      </p>
                      <p>
                        <strong>Location:</strong>
                        {selectedVfcData?.location || "N/A"}
                      </p>
                      <p>
                        <strong>Address:</strong>
                        {selectedVfcData?.address || "N/A"}
                      </p>
                      <p>
                        <strong>Pin Code:</strong>
                        {selectedVfcData?.pinCode || "N/A"}
                      </p>
                      <p>
                        <strong>Date of Establishment:</strong>
                        {selectedVfcData?.dateOfEstablishment
                          ? new Date(
                              selectedVfcData.dateOfEstablishment
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <p className="card-description">Cash Book Transactions</p>
                  <ListGroup variant="flush">
                    {transactions.map((txn) => (
                      <ListGroup.Item key={txn._id}>
                        <Row>
                          <Col>
                            <strong>Date:</strong>
                            {new Date(txn.transactionDate).toLocaleDateString()}
                          </Col>
                          <Col>
                            <strong>Type:</strong>{" "}
                            {txn.voucherType.voucherName}
                          </Col>
                          <Col>
                            <strong>Amount:</strong> {txn.amount}
                          </Col>
                        </Row>
                        <Row>
                          {/* <Col>
                            <strong>Description:</strong> {txn.description}
                          </Col> */}
                          <Col>
                            <strong>Party Details:</strong> {txn.partyDetails}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              </div>
            </Col>
            <Col md={8}>
              <div className="card">
                <div className="card-body">
                  <p className="card-description">Receipts and Payments</p>
                  <Row>
                    <Col md={6}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={receiptData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#82ca9d"
                            dataKey="value"
                          >
                            {receiptData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                            <LabelList
                              dataKey="value"
                              position="outside"
                              formatter={(value) => `${value.toLocaleString()}`}
                              style={{ fontSize: "14px", fontWeight: "bold" }}
                            />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <h5 className="text-center">Receipts</h5>
                    </Col>
                    <Col md={6}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={paymentData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {paymentData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                            <LabelList
                              dataKey="value"
                              position="outside"
                              formatter={(value) => `${value.toLocaleString()}`}
                              style={{ fontSize: "14px", fontWeight: "bold" }}
                            />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <h5 className="text-center">Payments</h5>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <p className="card-description">Transaction Trends</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={transactionTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="amount" stroke="#8884d8" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Col>
          </Row>
        </>
      </Container>
    </div>
  );
};

export default TransactionProfile;
